import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Hotel, Bed, Calendar, Users, Plus, Trash2, CheckCircle2, 
  AlertTriangle, Loader2, User, Phone, Mail, MapPin, CreditCard,
  Clock, Building2, Sparkles, Camera, Upload, X, Eye, Image as ImageIcon,
  IndianRupee, ShieldCheck
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import PhoneInput from '@/components/PhoneInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SelfCheckInPage() {
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const verificationToken = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requiresVerification, setRequiresVerification] = useState(false);
  const [data, setData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [checkedIn, setCheckedIn] = useState(false);
  const [checkInResult, setCheckInResult] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [paymentId, setPaymentId] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  
  // Form state
  const [primaryGuest, setPrimaryGuest] = useState({
    name: '',
    phone: '',
    email: '',
    id_type: '',
    id_number: '',
    id_proof_image: '',
    address: ''
  });
  const [additionalGuests, setAdditionalGuests] = useState([]);
  const [specialRequests, setSpecialRequests] = useState('');
  const [arrivalTime, setArrivalTime] = useState('');
  
  // File input refs
  const primaryCameraRef = useRef(null);
  const primaryFileRef = useRef(null);

  useEffect(() => {
    loadCheckInDetails();
  }, [code, verificationToken]);

  const loadCheckInDetails = async () => {
    setLoading(true);
    setError(null);
    setRequiresVerification(false);
    
    try {
      // Add token to request if available
      const url = verificationToken 
        ? `${API_URL}/api/self-checkin/${code}?token=${verificationToken}`
        : `${API_URL}/api/self-checkin/${code}`;
      const res = await axios.get(url);
      setData(res.data);
      
      // Pre-fill primary guest if available
      if (res.data.primary_guest) {
        setPrimaryGuest({
          name: res.data.primary_guest.name || '',
          phone: res.data.primary_guest.phone || '',
          email: res.data.primary_guest.email || '',
          id_type: '',
          id_number: '',
          id_proof_image: '',
          address: ''
        });
      }
    } catch (err) {
      const errorDetail = err.response?.data?.detail || 'Failed to load check-in details';
      
      // Check if OTP verification is required
      if (err.response?.status === 403 && errorDetail.includes('OTP verification required')) {
        setRequiresVerification(true);
        setError('Please verify your identity via OTP before proceeding with check-in.');
      } else {
        setError(errorDetail);
      }
    } finally {
      setLoading(false);
    }
  };

  // ID Document Upload Handler
  const handleIdUpload = async (file, guestType, guestIndex = null) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: JPEG, PNG, WebP, PDF');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum 10MB allowed.');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await axios.post(`${API_URL}/api/self-checkin/${code}/upload-id`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const documentUrl = res.data.document_url;

      if (guestType === 'primary') {
        setPrimaryGuest(prev => ({ ...prev, id_proof_image: documentUrl }));
      } else if (guestType === 'additional' && guestIndex !== null) {
        const updated = [...additionalGuests];
        updated[guestIndex] = { ...updated[guestIndex], id_proof_image: documentUrl };
        setAdditionalGuests(updated);
      }

      toast.success('ID document uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload ID document');
      console.error('Upload error:', error);
    }
  };

  const removeIdDocument = (guestType, guestIndex = null) => {
    if (guestType === 'primary') {
      setPrimaryGuest(prev => ({ ...prev, id_proof_image: '' }));
    } else if (guestType === 'additional' && guestIndex !== null) {
      const updated = [...additionalGuests];
      updated[guestIndex] = { ...updated[guestIndex], id_proof_image: '' };
      setAdditionalGuests(updated);
    }
  };

  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  // Load Razorpay script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // Handle Payment
  const handlePayment = async () => {
    if (!data?.payment?.razorpay_enabled || !data?.payment?.balance_due) {
      toast.error('Payment not available');
      return;
    }

    setPaymentProcessing(true);

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load payment gateway');
      }

      // Create order
      const orderRes = await axios.post(`${API_URL}/api/self-checkin/${code}/create-payment`, {
        amount: data.payment.balance_due
      });

      const { razorpay_order_id, amount, razorpay_key_id } = orderRes.data;

      const options = {
        key: razorpay_key_id,
        amount: amount,
        currency: 'INR',
        name: data?.hotel?.name || 'Hotel',
        description: `Room ${data?.room?.room_number} - Advance Payment`,
        order_id: razorpay_order_id,
        handler: async function (response) {
          try {
            // Verify payment
            const verifyRes = await axios.post(`${API_URL}/api/self-checkin/${code}/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              amount: amount
            });

            setPaymentComplete(true);
            setPaymentId(response.razorpay_payment_id);
            toast.success('Payment successful!');

            // Update balance due in local state
            setData(prev => ({
              ...prev,
              booking: { ...prev.booking, advance_paid: (prev.booking?.advance_paid || 0) + (amount / 100), balance_due: 0 },
              payment: { ...prev.payment, balance_due: 0 }
            }));
          } catch (error) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: primaryGuest.name,
          email: primaryGuest.email,
          contact: primaryGuest.phone
        },
        theme: {
          color: '#6366f1'
        },
        modal: {
          ondismiss: function() {
            setPaymentProcessing(false);
            toast.info('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        toast.error(`Payment failed: ${response.error.description}`);
        setPaymentProcessing(false);
      });
      razorpay.open();

    } catch (error) {
      toast.error('Failed to initiate payment');
      console.error('Payment error:', error);
    } finally {
      setPaymentProcessing(false);
    }
  };

  const addAdditionalGuest = () => {
    if (additionalGuests.length >= (data?.max_additional_guests || 0)) {
      toast.error(`Maximum ${data?.room_capacity} guests allowed for this room`);
      return;
    }
    setAdditionalGuests([...additionalGuests, {
      name: '',
      phone: '',
      email: '',
      id_type: '',
      id_number: '',
      id_proof_image: '',
      address: ''
    }]);
  };

  const removeAdditionalGuest = (index) => {
    setAdditionalGuests(additionalGuests.filter((_, i) => i !== index));
  };

  const updateAdditionalGuest = (index, field, value) => {
    const updated = [...additionalGuests];
    updated[index] = { ...updated[index], [field]: value };
    setAdditionalGuests(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation - Primary Guest
    if (!primaryGuest.name.trim()) {
      toast.error('Please enter guest name');
      return;
    }
    
    // Address is mandatory for self check-in
    if (!primaryGuest.address || 
        (typeof primaryGuest.address === 'string' && !primaryGuest.address.trim()) ||
        (typeof primaryGuest.address === 'object' && !primaryGuest.address.address_line1?.trim())) {
      toast.error('Please enter your address');
      return;
    }
    
    // ID Image is mandatory for self check-in
    if (!primaryGuest.id_proof_image) {
      toast.error('Please upload ID proof image');
      return;
    }
    
    // Validate additional guests - Name, ID Type, ID Number, ID Image required. Phone NOT required.
    for (let i = 0; i < additionalGuests.length; i++) {
      const ag = additionalGuests[i];
      if (!ag.name.trim()) {
        toast.error(`Please enter name for additional guest ${i + 1}`);
        return;
      }
      if (!ag.id_type?.trim()) {
        toast.error(`Please select ID type for additional guest ${i + 1}`);
        return;
      }
      if (!ag.id_number?.trim()) {
        toast.error(`Please enter ID number for additional guest ${i + 1}`);
        return;
      }
      if (!ag.id_proof_image) {
        toast.error(`Please upload ID proof image for additional guest ${i + 1}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const submitData = {
        primary_guest: primaryGuest,
        additional_guests: additionalGuests,
        special_requests: specialRequests || null,
        expected_arrival_time: arrivalTime || null,
        advance_payment_id: paymentId || null
      };

      const res = await axios.post(`${API_URL}/api/self-checkin/${code}/submit`, submitData);
      setCheckInResult(res.data);
      setCheckedIn(true);
      toast.success('Check-in successful!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-in failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-indigo-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading check-in details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    // Special handling for OTP verification required
    if (requiresVerification) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
          <Card className="max-w-md w-full shadow-xl border-0">
            <CardContent className="pt-8 text-center">
              <div className="h-20 w-20 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="h-10 w-10 text-indigo-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Verification Required</h2>
              <p className="text-gray-600 mb-6">
                For your security, please verify your identity via OTP before proceeding with self check-in.
              </p>
              <Button 
                onClick={() => navigate(`/self-checkin?code=${code}`)}
                className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
              >
                <ShieldCheck className="h-4 w-4 mr-2" />
                Verify with OTP
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl border-0">
          <CardContent className="pt-8 text-center">
            <div className="h-20 w-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="h-10 w-10 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Unable to Load</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={loadCheckInDetails} className="bg-indigo-500 hover:bg-indigo-600">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (checkedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        <Card className="max-w-md w-full shadow-xl border-0">
          <CardContent className="pt-8 text-center">
            <div className="h-24 w-24 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6 animate-bounce">
              <CheckCircle2 className="h-14 w-14 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Check-in Complete!</h2>
            <p className="text-gray-600 mb-6">Welcome to {data?.hotel?.name}</p>
            
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-6 text-white mb-6">
              <p className="text-sm opacity-80 mb-1">Your Room</p>
              <p className="text-4xl font-bold font-mono">{checkInResult?.room_number}</p>
              {data?.room?.block_name && (
                <p className="text-sm opacity-80 mt-1">{data.room.block_name}</p>
              )}
            </div>

            <div className="space-y-3 text-left bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-indigo-500" />
                <div>
                  <p className="text-xs text-gray-500">Check-in Time</p>
                  <p className="text-sm font-medium">{new Date(checkInResult?.check_in_time).toLocaleString('en-IN')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-indigo-500" />
                <div>
                  <p className="text-xs text-gray-500">Floor</p>
                  <p className="text-sm font-medium">{data?.room?.floor}</p>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-500 mt-6">
              Please proceed to the reception for your room key
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main check-in form
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            {data?.hotel?.logo_url ? (
              <img 
                src={data.hotel.logo_url} 
                alt={data.hotel.name}
                className="h-12 w-12 rounded-xl object-cover shadow"
              />
            ) : (
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow">
                <Hotel className="h-6 w-6 text-white" />
              </div>
            )}
            <div>
              <h1 className="font-bold text-gray-800 text-lg">{data?.hotel?.name}</h1>
              <p className="text-xs text-gray-500">Self Check-in</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 pb-24">
        {/* Booking Info Card */}
        <Card className="mb-6 shadow-lg border-0 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-80">Room</p>
                <p className="text-3xl font-bold font-mono">{data?.room?.room_number}</p>
              </div>
              <div className="text-right">
                <Badge className="bg-white/20 text-white border-0">{data?.room?.room_type}</Badge>
                {data?.room?.block_name && (
                  <p className="text-sm mt-1 opacity-80">{data.room.block_name}</p>
                )}
              </div>
            </div>
          </div>
          <CardContent className="pt-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-indigo-500" />
                <div>
                  <p className="text-xs text-gray-500">Check-in</p>
                  <p className="font-medium">{formatDate(data?.booking?.check_in)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-purple-500" />
                <div>
                  <p className="text-xs text-gray-500">Check-out</p>
                  <p className="font-medium">{formatDate(data?.booking?.check_out)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-emerald-500" />
                <div>
                  <p className="text-xs text-gray-500">Capacity</p>
                  <p className="font-medium">{data?.room_capacity} Guests</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-orange-500" />
                <div>
                  <p className="text-xs text-gray-500">Floor</p>
                  <p className="font-medium">{data?.room?.floor}</p>
                </div>
              </div>
            </div>
            
            {data?.room?.amenities?.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs text-gray-500 mb-2">Room Amenities</p>
                <div className="flex flex-wrap gap-1">
                  {data.room.amenities.slice(0, 6).map((amenity, idx) => (
                    <span key={idx} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded-full">
                      {amenity}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Check-in Form */}
        <form onSubmit={handleSubmit}>
          {/* Primary Guest */}
          <Card className="mb-4 shadow-md border-0">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <User className="h-4 w-4 text-indigo-500" />
                Primary Guest
              </CardTitle>
              <CardDescription>Your details for check-in</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-xs text-gray-600">Full Name *</Label>
                  <Input
                    value={primaryGuest.name}
                    onChange={(e) => setPrimaryGuest({...primaryGuest, name: e.target.value})}
                    placeholder="Enter your name"
                    required
                    className="mt-1"
                    data-testid="primary-guest-name"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-xs text-gray-600">Phone Number</Label>
                  <PhoneInput
                    value={primaryGuest.phone}
                    onChange={(value) => setPrimaryGuest({...primaryGuest, phone: value})}
                    placeholder="98765 43210"
                    className="mt-1"
                    data-testid="primary-guest-phone"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Email</Label>
                <Input
                  type="email"
                  value={primaryGuest.email}
                  onChange={(e) => setPrimaryGuest({...primaryGuest, email: e.target.value})}
                  placeholder="your@email.com"
                  className="mt-1"
                  data-testid="primary-guest-email"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-600">ID Type *</Label>
                  <Select 
                    value={primaryGuest.id_type} 
                    onValueChange={(v) => setPrimaryGuest({...primaryGuest, id_type: v})}
                  >
                    <SelectTrigger className="mt-1" data-testid="primary-guest-id-type">
                      <SelectValue placeholder="Select ID" />
                    </SelectTrigger>
                    <SelectContent>
                      {data?.id_types?.map((type) => (
                        <SelectItem key={type.id} value={type.name}>{type.name}</SelectItem>
                      ))}
                      <SelectItem value="Aadhaar">Aadhaar</SelectItem>
                      <SelectItem value="Passport">Passport</SelectItem>
                      <SelectItem value="Driving License">Driving License</SelectItem>
                      <SelectItem value="Voter ID">Voter ID</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-gray-600">ID Number *</Label>
                  <Input
                    value={primaryGuest.id_number}
                    onChange={(e) => setPrimaryGuest({...primaryGuest, id_number: e.target.value})}
                    placeholder="ID number"
                    className="mt-1"
                    data-testid="primary-guest-id-number"
                    required
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Address *</Label>
                <Textarea
                  value={primaryGuest.address}
                  onChange={(e) => setPrimaryGuest({...primaryGuest, address: e.target.value})}
                  placeholder="Your address"
                  rows={2}
                  className="mt-1"
                  data-testid="primary-guest-address"
                />
              </div>
              
              {/* ID Document Photo Upload */}
              <div className="pt-2 border-t">
                <Label className="text-xs text-gray-600 flex items-center gap-1 mb-2">
                  <ImageIcon className="h-3 w-3" />
                  ID Document Photo *
                </Label>
                {primaryGuest.id_proof_image ? (
                  <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200">
                    <div 
                      className="h-14 w-14 rounded-lg bg-white border border-green-200 overflow-hidden cursor-pointer flex items-center justify-center"
                      onClick={() => setPreviewImage(getFullUrl(primaryGuest.id_proof_image))}
                    >
                      {primaryGuest.id_proof_image.endsWith('.pdf') ? (
                        <span className="text-xs text-gray-500 font-medium">PDF</span>
                      ) : (
                        <img 
                          src={getFullUrl(primaryGuest.id_proof_image)} 
                          alt="ID" 
                          className="h-full w-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-700">ID Uploaded</p>
                      <p className="text-xs text-green-600">Tap to preview</p>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setPreviewImage(getFullUrl(primaryGuest.id_proof_image))}
                        className="h-8 w-8 text-green-600"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeIdDocument('primary')}
                        className="h-8 w-8 text-red-500"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => primaryCameraRef.current?.click()}
                      className="flex-1 border-blue-200 text-blue-600 hover:bg-blue-50"
                      data-testid="primary-camera-btn"
                    >
                      <Camera className="h-4 w-4 mr-1" />
                      Camera
                    </Button>
                    <input
                      ref={primaryCameraRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => handleIdUpload(e.target.files?.[0], 'primary')}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => primaryFileRef.current?.click()}
                      className="flex-1 border-purple-200 text-purple-600 hover:bg-purple-50"
                      data-testid="primary-upload-btn"
                    >
                      <Upload className="h-4 w-4 mr-1" />
                      Upload
                    </Button>
                    <input
                      ref={primaryFileRef}
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => handleIdUpload(e.target.files?.[0], 'primary')}
                      className="hidden"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Additional Guests */}
          {data?.max_additional_guests > 0 && (
            <Card className="mb-4 shadow-md border-0">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="h-4 w-4 text-purple-500" />
                      Additional Guests
                    </CardTitle>
                    <CardDescription>
                      {additionalGuests.length}/{data?.max_additional_guests} additional guests
                    </CardDescription>
                  </div>
                  {additionalGuests.length < data?.max_additional_guests && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={addAdditionalGuest}
                      className="text-indigo-600 border-indigo-200"
                      data-testid="add-guest-btn"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  )}
                </div>
              </CardHeader>
              {additionalGuests.length > 0 && (
                <CardContent>
                  <div className="text-xs text-purple-700 bg-purple-50 p-2 rounded mb-3">
                    * Name, ID Type, ID Number, and ID Photo are required for all guests
                  </div>
                  <ScrollArea className={additionalGuests.length > 1 ? "h-auto max-h-96" : ""}>
                    <div className="space-y-4">
                      {additionalGuests.map((guest, index) => (
                        <div key={index} className="p-4 bg-gray-50 rounded-xl relative">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeAdditionalGuest(index)}
                            className="absolute top-2 right-2 h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          <p className="text-xs font-medium text-gray-500 mb-3">Guest {index + 2}</p>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Input
                                value={guest.name}
                                onChange={(e) => updateAdditionalGuest(index, 'name', e.target.value)}
                                placeholder="Name *"
                                className="bg-white"
                                data-testid={`additional-guest-${index}-name`}
                                required
                              />
                            </div>
                            <div>
                              <PhoneInput
                                value={guest.phone}
                                onChange={(value) => updateAdditionalGuest(index, 'phone', value)}
                                placeholder="Phone (optional)"
                                className="bg-white"
                                data-testid={`additional-guest-${index}-phone`}
                              />
                            </div>
                            <div>
                              <Select 
                                value={guest.id_type} 
                                onValueChange={(v) => updateAdditionalGuest(index, 'id_type', v)}
                              >
                                <SelectTrigger className="bg-white">
                                  <SelectValue placeholder="ID Type *" />
                                </SelectTrigger>
                                <SelectContent>
                                  {data?.id_types?.map((type) => (
                                    <SelectItem key={type.id} value={type.name}>{type.name}</SelectItem>
                                  ))}
                                  <SelectItem value="Aadhaar">Aadhaar</SelectItem>
                                  <SelectItem value="Passport">Passport</SelectItem>
                                  <SelectItem value="Driving License">Driving License</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Input
                                value={guest.id_number}
                                onChange={(e) => updateAdditionalGuest(index, 'id_number', e.target.value)}
                                placeholder="ID Number *"
                                className="bg-white"
                                required
                              />
                            </div>
                            {/* Additional Guest ID Photo Upload */}
                            <div className="col-span-2 pt-2 border-t border-gray-200">
                              <Label className="text-xs text-gray-500 flex items-center gap-1 mb-2">
                                <ImageIcon className="h-3 w-3" />
                                ID Document Photo *
                              </Label>
                              {guest.id_proof_image ? (
                                <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200">
                                  <div 
                                    className="h-12 w-12 rounded-lg bg-white border border-green-200 overflow-hidden cursor-pointer flex items-center justify-center"
                                    onClick={() => setPreviewImage(getFullUrl(guest.id_proof_image))}
                                  >
                                    {guest.id_proof_image.endsWith('.pdf') ? (
                                      <span className="text-xs text-gray-500">PDF</span>
                                    ) : (
                                      <img 
                                        src={getFullUrl(guest.id_proof_image)} 
                                        alt="ID" 
                                        className="h-full w-full object-cover"
                                      />
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-xs font-medium text-green-700">ID Uploaded</p>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => removeIdDocument('additional', index)}
                                    className="h-7 w-7 text-red-500"
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const input = document.createElement('input');
                                      input.type = 'file';
                                      input.accept = 'image/*';
                                      input.capture = 'environment';
                                      input.onchange = (e) => handleIdUpload(e.target.files?.[0], 'additional', index);
                                      input.click();
                                    }}
                                    className="flex-1 text-xs border-blue-200 text-blue-600 hover:bg-blue-50"
                                  >
                                    <Camera className="h-3 w-3 mr-1" />
                                    Camera
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const input = document.createElement('input');
                                      input.type = 'file';
                                      input.accept = 'image/*,application/pdf';
                                      input.onchange = (e) => handleIdUpload(e.target.files?.[0], 'additional', index);
                                      input.click();
                                    }}
                                    className="flex-1 text-xs border-purple-200 text-purple-600 hover:bg-purple-50"
                                  >
                                    <Upload className="h-3 w-3 mr-1" />
                                    Upload
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              )}
            </Card>
          )}

          {/* Payment Section */}
          {data?.payment?.razorpay_enabled && data?.payment?.balance_due > 0 && (
            <Card className="mb-4 shadow-md border-0 overflow-hidden">
              <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-4 text-white">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  <span className="font-semibold">Pay Advance</span>
                </div>
              </div>
              <CardContent className="pt-4 space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-500">Total Amount</p>
                    <p className="text-xl font-bold">{formatCurrency(data?.booking?.total_amount)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Balance Due</p>
                    <p className="text-xl font-bold text-emerald-600">{formatCurrency(data?.payment?.balance_due)}</p>
                  </div>
                </div>

                {paymentComplete ? (
                  <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl border border-green-200">
                    <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-green-700">Payment Successful!</p>
                      <p className="text-xs text-green-600">Transaction ID: {paymentId}</p>
                    </div>
                  </div>
                ) : (
                  <Button
                    type="button"
                    onClick={handlePayment}
                    disabled={paymentProcessing}
                    className="w-full h-12 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                    data-testid="pay-advance-btn"
                  >
                    {paymentProcessing ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <IndianRupee className="h-5 w-5 mr-1" />
                        Pay {formatCurrency(data?.payment?.balance_due)}
                      </>
                    )}
                  </Button>
                )}
                
                <p className="text-xs text-gray-500 text-center">
                  {paymentComplete ? 'You can now complete check-in' : 'Payment is optional. You can pay at checkout.'}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Special Requests */}
          <Card className="mb-6 shadow-md border-0">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-orange-500" />
                Additional Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs text-gray-600">Expected Arrival Time</Label>
                <Input
                  type="time"
                  value={arrivalTime}
                  onChange={(e) => setArrivalTime(e.target.value)}
                  className="mt-1"
                  data-testid="arrival-time"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Special Requests</Label>
                <Textarea
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  placeholder="Any special requests or preferences..."
                  rows={3}
                  className="mt-1"
                  data-testid="special-requests"
                />
              </div>
            </CardContent>
          </Card>
        </form>
      </main>

      {/* Fixed Bottom Submit Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t p-4 shadow-lg">
        <div className="max-w-lg mx-auto">
          <Button 
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 shadow-lg"
            data-testid="submit-checkin-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-5 w-5 mr-2" />
                Complete Check-in
              </>
            )}
          </Button>
          <p className="text-center text-xs text-gray-500 mt-2">
            {1 + additionalGuests.length} of {data?.room_capacity} guests
          </p>
        </div>
      </div>

      {/* ID Document Preview Dialog */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>ID Document Preview</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center min-h-[300px] bg-gray-100 rounded-lg">
            {previewImage?.endsWith('.pdf') ? (
              <div className="text-center">
                <p className="text-gray-500 mb-2">PDF Document</p>
                <Button
                  variant="outline"
                  onClick={() => window.open(previewImage, '_blank')}
                >
                  Open PDF
                </Button>
              </div>
            ) : (
              <img 
                src={previewImage} 
                alt="ID Document" 
                className="max-h-[400px] max-w-full object-contain rounded-lg"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
